'use client';

import { c as _c } from "react/compiler-runtime";
import { zodResolver } from '@hookform/resolvers/zod';
import { useImperativeHandle, useRef } from 'react';
import { EmptyObject, ErrorOption, FieldValues, FormProvider, SubmitHandler, UseFormProps, UseFormReturn, useForm, useFormState } from 'react-hook-form';
import { ZodType } from 'zod';
import { trackClick } from '@sola/tracking/components/datalayer.tsx';
import { getTextFromReactNode } from '@sola/utils/react.ts';
import { ButtonStyle, ButtonStyleProps } from '../../atoms/button-style/button-style.tsx';
import { Alert } from '../alert/alert.tsx';

/**
 * Form's internal methods to be publically exposed to parent.
 */
export interface FormPublicMethods extends Pick<UseFormReturn, 'clearErrors' | 'formState' | 'getFieldState' | 'getValues' | 'reset' | 'resetField' | 'setError' | 'setValue' | 'trigger' | 'watch'> {
  setGeneralError: (error: ErrorOption, config?: {
    shouldFocus: boolean;
  }) => void;
}

/**
 * Function that creates a ref to be passed to a Form's formMethods prop.
 * This allows for parent components to have access to a form's internal methods.
 */
export const useFormMethods = () => {
  return useRef(null);
};
export const FormError: React.FC = () => {
  const $ = _c(2);
  const {
    errors
  } = useFormState();
  if (!errors?.root) {
    return null;
  }
  let t0;
  if ($[0] !== errors.root.error.message) {
    t0 = <div className="Form__error my-comfy-sm"><Alert variant="negative" dismissible={false} message={errors.root.error.message} /></div>;
    $[0] = errors.root.error.message;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export interface FormProps extends Omit<React.ComponentPropsWithoutRef<'form'>, 'onSubmit'> {
  /**
   * Ref for internal <form> element.
   */
  formRef?: React.Ref<HTMLFormElement> | null;
  /**
   * Ref that will be assigned form's internal methods and state.
   */
  formMethods?: React.Ref<EmptyObject | FormPublicMethods>;
  isCustomError?: boolean;
  onSubmit: SubmitHandler<FieldValues>;
  /**
   * React hook form useForm options.
   */
  options?: Omit<UseFormProps, 'resolver' | 'context'>;
  validationSchema?: ZodType;
}
/**
 * Form component.
 * It is recommended to landmark your form to provide an accessible name
 * You can do this by either providing aria-labelledby, aria-label, or <title>
 */
export const Form = t0 => {
  const $ = _c(31);
  let children;
  let formMethods;
  let formProps;
  let formRef;
  let isCustomError;
  let onSubmit;
  let options;
  let validationSchema;
  if ($[0] !== t0) {
    ({
      children,
      formRef,
      formMethods,
      isCustomError,
      onSubmit,
      options,
      validationSchema,
      ...formProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = formMethods;
    $[3] = formProps;
    $[4] = formRef;
    $[5] = isCustomError;
    $[6] = onSubmit;
    $[7] = options;
    $[8] = validationSchema;
  } else {
    children = $[1];
    formMethods = $[2];
    formProps = $[3];
    formRef = $[4];
    isCustomError = $[5];
    onSubmit = $[6];
    options = $[7];
    validationSchema = $[8];
  }
  let t1;
  if ($[9] !== validationSchema) {
    t1 = validationSchema && {
      resolver: zodResolver(validationSchema as any)
    };
    $[9] = validationSchema;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  let t2;
  if ($[11] !== options || $[12] !== t1) {
    t2 = {
      ...t1,
      mode: "onBlur",
      shouldUnregister: true,
      ...options
    };
    $[11] = options;
    $[12] = t1;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const methods = useForm(t2);
  let t3;
  if ($[14] !== methods) {
    t3 = () => ({
      clearErrors: methods.clearErrors,
      formState: methods.formState,
      getFieldState: methods.getFieldState,
      getValues: methods.getValues,
      register: methods.register,
      reset: methods.reset,
      resetField: methods.resetField,
      setError: methods.setError,
      setGeneralError: (error, t4) => {
        const config = t4 === undefined ? undefined : t4;
        methods.setError("root.error", error, config);
      },
      setValue: methods.setValue,
      trigger: methods.trigger,
      unregister: methods.unregister,
      watch: methods.watch
    });
    $[14] = methods;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  useImperativeHandle(formMethods, t3, t4);
  let t5;
  if ($[17] !== isCustomError) {
    t5 = !isCustomError && <FormError />;
    $[17] = isCustomError;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== methods || $[20] !== onSubmit) {
    t6 = methods.handleSubmit(onSubmit);
    $[19] = methods;
    $[20] = onSubmit;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== children || $[23] !== formProps || $[24] !== formRef || $[25] !== t6) {
    t7 = <form ref={formRef} noValidate={true} onSubmit={t6} className="flex flex-col gap-comfy-sm" {...formProps}>{children}</form>;
    $[22] = children;
    $[23] = formProps;
    $[24] = formRef;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== methods || $[28] !== t5 || $[29] !== t7) {
    t8 = <FormProvider {...methods}>{t5}{t7}</FormProvider>;
    $[27] = methods;
    $[28] = t5;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  return t8;
};
export interface FormSubmitProps extends ButtonStyleProps {
  children: React.ReactNode;
  className?: string;
  disableUntilFilled?: boolean;
}
/**
 * Form's submission button, which has access to internal state
 */
export const FormSubmit: React.FC<FormSubmitProps> = t0 => {
  const $ = _c(16);
  let buttonProps;
  let children;
  let disableUntilFilled;
  let disabled;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      disabled,
      disableUntilFilled,
      fullWidth: t1,
      ...buttonProps
    } = t0);
    $[0] = t0;
    $[1] = buttonProps;
    $[2] = children;
    $[3] = disableUntilFilled;
    $[4] = disabled;
    $[5] = t1;
  } else {
    buttonProps = $[1];
    children = $[2];
    disableUntilFilled = $[3];
    disabled = $[4];
    t1 = $[5];
  }
  const fullWidth = t1 === undefined ? true : t1;
  const {
    isValid
  } = useFormState();
  let isDisabled = disabled;
  if (!isDisabled) {
    isDisabled = !disableUntilFilled ? false : !isValid;
  }
  let t2;
  if ($[6] !== children) {
    t2 = () => {
      trackClick(getTextFromReactNode(children) || "Form Submit");
    };
    $[6] = children;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children || $[9] !== t2) {
    t3 = <button onClick={t2} type="submit">{children}</button>;
    $[8] = children;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== buttonProps || $[12] !== fullWidth || $[13] !== isDisabled || $[14] !== t3) {
    t4 = <ButtonStyle fullWidth={fullWidth} {...buttonProps} disabled={isDisabled}>{t3}</ButtonStyle>;
    $[11] = buttonProps;
    $[12] = fullWidth;
    $[13] = isDisabled;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};